import {
  Habit,
  ID,
  RepeatingTask,
  Task,
  TaskType,
  Timeslot,
  Timestamp,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import { isSameDay } from 'date-fns';

import { getTaskDueDate } from './get-task-due-date';
import { getTaskRescheduleUpdate } from './get-task-reschedule-update';
import { getScheduleByDate } from './schedules';

// `undefined` for startTime/duration/reminderTime means "leave this field as it
// is" (date-only changes preserve the task's existing time and reminder). An
// explicit value — including `null` — overrides the field.
export type RescheduleTasksInput = {
  startDate: Timestamp | null;
  startTime?: number | null;
  duration?: number | null;
  reminderTime?: number | null;
};

export type GetTasksRescheduleUpdatesOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  // The date each task's row was shown under (date-grouped views). Identifies
  // which occurrence to move; falls back to the schedule due date when absent.
  displayDates?: ReadonlyMap<ID, Timestamp>;
};

const getExistingTimeslot = (
  task: Task,
  originalDate: Timestamp | undefined,
  today: Timestamp,
): Timeslot | null => {
  if (task.type === TaskType.Task) {
    return task.timeslot ?? null;
  }

  if (!originalDate) {
    return null;
  }

  const scheduledTask = task as Habit | RepeatingTask;
  const schedule = getScheduleByDate(scheduledTask.schedules, {
    date: originalDate,
    today,
  });
  const occurrence = schedule?.plannedOccurrences?.find((plannedOccurrence) =>
    isSameDay(plannedOccurrence.date, originalDate),
  );

  return occurrence?.timeslot ?? null;
};

export const getTasksRescheduleUpdates = (
  tasks: Task[],
  { startDate, startTime, duration, reminderTime }: RescheduleTasksInput,
  options: GetTasksRescheduleUpdatesOptions,
): UpdateTask[] => {
  if (!tasks.length) {
    return [];
  }

  const hasTimeslotOverride = startTime !== undefined || duration !== undefined;
  const overrideTimeslot: Timeslot | null =
    (startTime ?? null) !== null || (duration ?? null) !== null
      ? { startTime: startTime ?? null, duration: duration ?? null }
      : null;

  const { displayDates, ...dueDateOptions } = options;

  return tasks.map((task) => {
    const originalDate =
      displayDates?.get(task.id) ?? getTaskDueDate(task, dueDateOptions);
    const timeslot = hasTimeslotOverride
      ? overrideTimeslot
      : getExistingTimeslot(task, originalDate, dueDateOptions.today);
    const update = getTaskRescheduleUpdate(task, startDate, timeslot, {
      ...dueDateOptions,
      originalDate,
    });

    return reminderTime === undefined
      ? update
      : ({ ...update, reminderTime } as UpdateTask);
  });
};
